/**
 * Copyright (c) 2018-2023 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Russell Parker <russell@benchling.com>
 */
import { __assign } from "tslib";
import { Subject } from 'rxjs';
import { Viewport } from '../../mol-canvas3d/camera/util';
import { Vec2, EPSILON } from '../../mol-math/linear-algebra';
import { BitFlags, noop } from '../../mol-util';
export function getButtons(event) {
    if (typeof event === 'object') {
        if ('buttons' in event) {
            return event.buttons;
        }
        else if ('which' in event) {
            var b = event.which; // 'any' to support older browsers
            if (b === 2) {
                return 4;
            }
            else if (b === 3) {
                return 2;
            }
            else if (b > 0) {
                return 1 << (b - 1);
            }
        }
    }
    return 0;
}
export function getButton(event) {
    if (typeof event === 'object') {
        if ('button' in event) {
            var b = event.button;
            if (b === 1) {
                return 4;
            }
            else if (b === 2) {
                return 2;
            }
            else if (b >= 0) {
                return 1 << b;
            }
        }
    }
    return 0;
}
export function getModifiers(event) {
    return {
        alt: 'altKey' in event ? event.altKey : false,
        shift: 'shiftKey' in event ? event.shiftKey : false,
        control: 'ctrlKey' in event ? event.ctrlKey : false,
        meta: 'metaKey' in event ? event.metaKey : false
    };
}
export var DefaultInputObserverProps = {
    noScroll: true,
    noMiddleClickScroll: true,
    noContextMenu: true,
    noPinchZoom: true,
    noTextSelect: true,
    preventGestures: false,
    mask: function (x, y) { return true; },
    pixelScale: 1
};
export var ModifiersKeys;
(function (ModifiersKeys) {
    ModifiersKeys.None = create();
    function areEqual(a, b) {
        return a.shift === b.shift && a.alt === b.alt && a.control === b.control && a.meta === b.meta;
    }
    ModifiersKeys.areEqual = areEqual;
    function areNone(a) {
        return areEqual(a, ModifiersKeys.None);
    }
    ModifiersKeys.areNone = areNone;
    function size(a) {
        if (!a)
            return 0;
        var ret = 0;
        if (!!a.shift)
            ret++;
        if (!!a.alt)
            ret++;
        if (!!a.control)
            ret++;
        if (!!a.meta)
            ret++;
        return ret;
    }
    ModifiersKeys.size = size;
    function create(modifierKeys) {
        if (modifierKeys === void 0) { modifierKeys = {}; }
        return {
            shift: !!modifierKeys.shift,
            alt: !!modifierKeys.alt,
            control: !!modifierKeys.control,
            meta: !!modifierKeys.meta
        };
    }
    ModifiersKeys.create = create;
})(ModifiersKeys || (ModifiersKeys = {}));
export var ButtonsType;
(function (ButtonsType) {
    ButtonsType.has = BitFlags.has;
    ButtonsType.create = BitFlags.create;
    var Flag;
    (function (Flag) {
        /** No button or un-initialized */
        Flag[Flag["None"] = 0] = "None";
        /** Primary button (usually left) */
        Flag[Flag["Primary"] = 1] = "Primary";
        /** Secondary button (usually right) */
        Flag[Flag["Secondary"] = 2] = "Secondary";
        /** Auxilary button (usually middle or mouse wheel button)  */
        Flag[Flag["Auxilary"] = 4] = "Auxilary";
        /** 4th button (typically the "Browser Back" button) */
        Flag[Flag["Forth"] = 8] = "Forth";
        /** 5th button (typically the "Browser Forward" button) */
        Flag[Flag["Five"] = 16] = "Five";
    })(Flag = ButtonsType.Flag || (ButtonsType.Flag = {}));
})(ButtonsType || (ButtonsType = {}));
export var EmptyKeyInput = {
    key: '',
    code: '',
    modifiers: ModifiersKeys.None,
    x: -1,
    y: -1,
    pageX: -1,
    pageY: -1,
    preventDefault: noop,
};
var DraggingState;
(function (DraggingState) {
    DraggingState[DraggingState["Stopped"] = 0] = "Stopped";
    DraggingState[DraggingState["Started"] = 1] = "Started";
    DraggingState[DraggingState["Moving"] = 2] = "Moving";
})(DraggingState || (DraggingState = {}));
function createEvents() {
    return {
        drag: new Subject(),
        interactionEnd: new Subject(),
        click: new Subject(),
        move: new Subject(),
        wheel: new Subject(),
        pinch: new Subject(),
        gesture: new Subject(),
        resize: new Subject(),
        leave: new Subject(),
        enter: new Subject(),
        modifiers: new Subject(),
        key: new Subject(),
        keyUp: new Subject(),
        keyDown: new Subject(),
        lock: new Subject(),
    };
}
var AllowedNonPrintableKeys = ['Backspace', 'Delete'];
var InputObserver;
(function (InputObserver) {
    function create(props) {
        if (props === void 0) { props = {}; }
        var _a = __assign(__assign({}, DefaultInputObserverProps), props), noScroll = _a.noScroll, noContextMenu = _a.noContextMenu;
        return __assign(__assign({ noScroll: noScroll, noContextMenu: noContextMenu, pointerLock: false, width: 0, height: 0, pixelRatio: 1 }, createEvents()), { requestPointerLock: noop, exitPointerLock: noop, dispose: noop });
    }
    InputObserver.create = create;
    function fromElement(element, props) {
        if (props === void 0) { props = {}; }
        var _a = __assign(__assign({}, DefaultInputObserverProps), props), noScroll = _a.noScroll, noMiddleClickScroll = _a.noMiddleClickScroll, noContextMenu = _a.noContextMenu, noPinchZoom = _a.noPinchZoom, noTextSelect = _a.noTextSelect, mask = _a.mask, pixelScale = _a.pixelScale, preventGestures = _a.preventGestures;
        var width = element.clientWidth * pixelRatio();
        var height = element.clientHeight * pixelRatio();
        var isLocked = false;
        var lockedViewport = Viewport();
        var lastTouchDistance = 0, lastTouchFraction = 0;
        var pointerDown = Vec2();
        var pointerStart = Vec2();
        var pointerEnd = Vec2();
        var pointerDelta = Vec2();
        var rectSize = Vec2();
        var modifierKeys = {
            shift: false,
            alt: false,
            control: false,
            meta: false
        };
        var position = {
            x: -1,
            y: -1,
            pageX: -1,
            pageY: -1,
        };
        function pixelRatio() {
            return window.devicePixelRatio * pixelScale;
        }
        function getModifierKeys() {
            return __assign({}, modifierKeys);
        }
        function getKeyOnElement(event) {
            return event.target === document.body || event.target === element;
        }
        var dragging = DraggingState.Stopped;
        var disposed = false;
        var buttons = ButtonsType.create(ButtonsType.Flag.None);
        var button = ButtonsType.Flag.None;
        var isInside = false;
        var hasMoved = false;
        var resizeObserver;
        if (typeof window.ResizeObserver !== 'undefined') {
            resizeObserver = new window.ResizeObserver(onResize);
        }
        var events = createEvents();
        var drag = events.drag, interactionEnd = events.interactionEnd, wheel = events.wheel, pinch = events.pinch, gesture = events.gesture, click = events.click, move = events.move, leave = events.leave, enter = events.enter, resize = events.resize, modifiers = events.modifiers, key = events.key, keyUp = events.keyUp, keyDown = events.keyDown, lock = events.lock;
        attach();
        function attach() {
            element.addEventListener('contextmenu', onContextMenu, false);
            element.addEventListener('wheel', onMouseWheel, false);
            element.addEventListener('mousedown', onMouseDown, false);
            // for dragging to work outside canvas bounds,
            // mouse move/up events have to be added to a parent, i.e. window
            window.addEventListener('mousemove', onMouseMove, false);
            window.addEventListener('mouseup', onMouseUp, false);
            element.addEventListener('touchstart', onTouchStart, false);
            element.addEventListener('touchmove', onTouchMove, false);
            element.addEventListener('touchend', onTouchEnd, false);
            element.addEventListener('gesturechange', onGestureChange, false);
            element.addEventListener('gesturestart', onGestureStart, false);
            element.addEventListener('gestureend', onGestureEnd, false);
            // reset buttons and modifier keys state when browser window looses focus
            window.addEventListener('blur', handleBlur);
            window.addEventListener('keyup', handleKeyUp, false);
            window.addEventListener('keydown', handleKeyDown, false);
            window.addEventListener('keypress', handleKeyPress, false);
            document.addEventListener('pointerlockchange', onPointerLockChange, false);
            document.addEventListener('pointerlockerror', onPointerLockError, false);
            if (resizeObserver != null) {
                resizeObserver.observe(element.parentElement);
            }
            else {
                window.addEventListener('resize', onResize, false);
            }
        }
        function dispose() {
            if (disposed)
                return;
            disposed = true;
            element.removeEventListener('contextmenu', onContextMenu, false);
            element.removeEventListener('wheel', onMouseWheel, false);
            element.removeEventListener('mousedown', onMouseDown, false);
            window.removeEventListener('mousemove', onMouseMove, false);
            window.removeEventListener('mouseup', onMouseUp, false);
            element.removeEventListener('touchstart', onTouchStart, false);
            element.removeEventListener('touchmove', onTouchMove, false);
            element.removeEventListener('touchend', onTouchEnd, false);
            element.removeEventListener('gesturechange', onGestureChange, false);
            element.removeEventListener('gesturestart', onGestureStart, false);
            element.removeEventListener('gestureend', onGestureEnd, false);
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('keyup', handleKeyUp, false);
            window.removeEventListener('keydown', handleKeyDown, false);
            window.removeEventListener('keypress', handleKeyPress, false);
            document.removeEventListener('pointerlockchange', onPointerLockChange, false);
            document.removeEventListener('pointerlockerror', onPointerLockError, false);
            cross.remove();
            if (resizeObserver != null) {
                resizeObserver.unobserve(element.parentElement);
                resizeObserver.disconnect();
            }
            else {
                window.removeEventListener('resize', onResize, false);
            }
        }
        function onPointerLockChange() {
            if (element.ownerDocument.pointerLockElement === element) {
                isLocked = true;
            }
            else {
                isLocked = false;
            }
            toggleCross(isLocked);
            lock.next(isLocked);
        }
        function onPointerLockError() {
            console.error('Unable to use Pointer Lock API');
            isLocked = false;
            toggleCross(isLocked);
            lock.next(isLocked);
        }
        function onContextMenu(event) {
            if (!mask(event.clientX, event.clientY))
                return;
            if (noContextMenu) {
                event.preventDefault();
            }
        }
        function updateModifierKeys(event) {
            modifierKeys.alt = event.altKey;
            modifierKeys.shift = event.shiftKey;
            modifierKeys.control = event.ctrlKey;
            modifierKeys.meta = event.metaKey;
        }
        function handleBlur() {
            if (buttons || modifierKeys.shift || modifierKeys.alt || modifierKeys.meta || modifierKeys.control) {
                buttons = 0;
                modifierKeys.shift = modifierKeys.alt = modifierKeys.control = modifierKeys.meta = false;
            }
        }
        function handleKeyDown(event) {
            var changed = false;
            if (!modifierKeys.alt && event.altKey) {
                changed = true;
                modifierKeys.alt = true;
            }
            if (!modifierKeys.shift && event.shiftKey) {
                changed = true;
                modifierKeys.shift = true;
            }
            if (!modifierKeys.control && event.ctrlKey) {
                changed = true;
                modifierKeys.control = true;
            }
            if (!modifierKeys.meta && event.metaKey) {
                changed = true;
                modifierKeys.meta = true;
            }
            if (changed && isInside)
                modifiers.next(getModifierKeys());
            if (getKeyOnElement(event) && isInside) {
                keyDown.next(__assign(__assign({ key: event.key, code: event.code, modifiers: getModifierKeys() }, position), { preventDefault: function () { return event.preventDefault(); } }));
            }
        }
        function handleKeyUp(event) {
            var changed = false;
            if (modifierKeys.alt && !event.altKey) {
                changed = true;
                modifierKeys.alt = false;
            }
            if (modifierKeys.shift && !event.shiftKey) {
                changed = true;
                modifierKeys.shift = false;
            }
            if (modifierKeys.control && !event.ctrlKey) {
                changed = true;
                modifierKeys.control = false;
            }
            if (modifierKeys.meta && !event.metaKey) {
                changed = true;
                modifierKeys.meta = false;
            }
            if (changed && isInside)
                modifiers.next(getModifierKeys());
            if (AllowedNonPrintableKeys.includes(event.key))
                handleKeyPress(event);
            if (getKeyOnElement(event) && isInside) {
                keyUp.next(__assign(__assign({ key: event.key, code: event.code, modifiers: getModifierKeys() }, position), { preventDefault: function () { return event.preventDefault(); } }));
            }
        }
        function handleKeyPress(event) {
            if (!getKeyOnElement(event) || !isInside)
                return;
            key.next(__assign(__assign({ key: event.key, code: event.code, modifiers: getModifierKeys() }, position), { preventDefault: function () { return event.preventDefault(); } }));
        }
        function getCenterTouch(ev) {
            var t0 = ev.touches[0];
            var t1 = ev.touches[1];
            return {
                clientX: (t0.clientX + t1.clientX) / 2,
                clientY: (t0.clientY + t1.clientY) / 2,
                pageX: (t0.pageX + t1.pageX) / 2,
                pageY: (t0.pageY + t1.pageY) / 2,
                target: ev.target
            };
        }
        function getTouchDistance(ev) {
            var dx = ev.touches[0].pageX - ev.touches[1].pageX;
            var dy = ev.touches[0].pageY - ev.touches[1].pageY;
            return Math.sqrt(dx * dx + dy * dy);
        }
        var singleTouchDistance = -1;
        var lastSingleTouch = undefined;
        var singleTouchPosition = Vec2(), singleTouchTmp = Vec2();
        function updateSingleTouchDistance(ev) {
            if (singleTouchDistance < 0)
                return;
            Vec2.set(singleTouchTmp, ev.touches[0].pageX, ev.touches[0].pageY);
            singleTouchDistance += Vec2.distance(singleTouchPosition, singleTouchTmp);
            Vec2.copy(singleTouchPosition, singleTouchTmp);
        }
        function onTouchStart(ev) {
            ev.preventDefault();
            lastSingleTouch = undefined;
            singleTouchDistance = -1;
            if (ev.touches.length === 1) {
                singleTouchDistance = 0;
                Vec2.set(singleTouchPosition, ev.touches[0].pageX, ev.touches[0].pageY);
                lastSingleTouch = ev.touches[0];
                buttons = button = ButtonsType.Flag.Primary;
                onPointerDown(ev.touches[0]);
            }
            else if (ev.touches.length === 2) {
                buttons = ButtonsType.Flag.Secondary & ButtonsType.Flag.Auxilary;
                button = ButtonsType.Flag.Secondary;
                onPointerDown(getCenterTouch(ev));
                var touchDistance = getTouchDistance(ev);
                lastTouchDistance = touchDistance;
                pinch.next({
                    distance: touchDistance,
                    fraction: 1,
                    fractionDelta: 0,
                    delta: 0,
                    isStart: true,
                    buttons: buttons,
                    button: button,
                    modifiers: getModifierKeys()
                });
            }
            else if (ev.touches.length === 3) {
                buttons = button = ButtonsType.Flag.Forth;
                onPointerDown(getCenterTouch(ev));
            }
        }
        function onTouchEnd(ev) {
            endDrag();
            if (lastSingleTouch && singleTouchDistance <= 4) {
                var t = lastSingleTouch;
                if (!mask(t.clientX, t.clientY))
                    return;
                eventOffset(singleTouchTmp, t);
                var _a = getPagePosition(t), pageX = _a.pageX, pageY = _a.pageY;
                var x = singleTouchTmp[0], y = singleTouchTmp[1];
                click.next({ x: x, y: y, pageX: pageX, pageY: pageY, buttons: buttons, button: button, modifiers: getModifierKeys() });
            }
            lastSingleTouch = undefined;
        }
        function onTouchMove(ev) {
            button = ButtonsType.Flag.None;
            if (noPinchZoom) {
                ev.preventDefault();
                ev.stopPropagation();
                if (ev.originalEvent) {
                    ev.originalEvent.preventDefault();
                    ev.originalEvent.stopPropagation();
                }
            }
            lastSingleTouch = undefined;
            if (ev.touches.length === 1) {
                buttons = ButtonsType.Flag.Primary;
                lastSingleTouch = ev.touches[0];
                updateSingleTouchDistance(ev);
                onPointerMove(ev.touches[0]);
            }
            else if (ev.touches.length === 2) {
                var touchDistance = getTouchDistance(ev);
                var touchDelta = lastTouchDistance - touchDistance;
                if (Math.abs(touchDelta) < 4) {
                    buttons = ButtonsType.Flag.Secondary;
                    onPointerMove(getCenterTouch(ev));
                }
                else {
                    buttons = ButtonsType.Flag.Auxilary;
                    updateModifierKeys(ev);
                    var fraction = lastTouchDistance / touchDistance;
                    pinch.next({
                        delta: touchDelta,
                        fraction: fraction,
                        fractionDelta: lastTouchFraction - fraction,
                        distance: touchDistance,
                        isStart: false,
                        buttons: buttons,
                        button: button,
                        modifiers: getModifierKeys()
                    });
                    lastTouchFraction = fraction;
                }
                lastTouchDistance = touchDistance;
            }
            else if (ev.touches.length === 3) {
                buttons = ButtonsType.Flag.Forth;
                onPointerMove(getCenterTouch(ev));
            }
        }
        function onMouseDown(ev) {
            updateModifierKeys(ev);
            buttons = getButtons(ev);
            button = getButton(ev);
            if (noMiddleClickScroll && buttons === ButtonsType.Flag.Auxilary) {
                ev.preventDefault;
            }
            onPointerDown(ev);
        }
        function onMouseMove(ev) {
            updateModifierKeys(ev);
            buttons = getButtons(ev);
            button = ButtonsType.Flag.None;
            onPointerMove(ev);
        }
        function onMouseUp(ev) {
            updateModifierKeys(ev);
            buttons = getButtons(ev);
            button = getButton(ev);
            onPointerUp(ev);
            endDrag();
        }
        function endDrag() {
            interactionEnd.next(void 0);
        }
        function onPointerDown(ev) {
            if (!mask(ev.clientX, ev.clientY))
                return;
            eventOffset(pointerStart, ev);
            Vec2.copy(pointerDown, pointerStart);
            if (insideBounds(pointerStart)) {
                dragging = DraggingState.Started;
            }
        }
        function onPointerUp(ev) {
            dragging = DraggingState.Stopped;
            if (!mask(ev.clientX, ev.clientY))
                return;
            eventOffset(pointerEnd, ev);
            if (!hasMoved && Vec2.distance(pointerEnd, pointerDown) < 4) {
                var _a = getPagePosition(ev), pageX = _a.pageX, pageY = _a.pageY;
                var x = pointerEnd[0], y = pointerEnd[1];
                click.next({ x: x, y: y, pageX: pageX, pageY: pageY, buttons: buttons, button: button, modifiers: getModifierKeys() });
            }
            hasMoved = false;
        }
        function onPointerMove(ev) {
            var _a;
            eventOffset(pointerEnd, ev);
            var _b = getPagePosition(ev), pageX = _b.pageX, pageY = _b.pageY;
            var x = pointerEnd[0], y = pointerEnd[1];
            var movementX = ev.movementX, movementY = ev.movementY;
            var inside = insideBounds(pointerEnd) && mask(ev.clientX, ev.clientY);
            if (isInside && !inside) {
                leave.next(void 0);
            }
            else if (!isInside && inside) {
                enter.next(void 0);
            }
            isInside = inside;
            position.x = x;
            position.y = y;
            position.pageX = pageX;
            position.pageY = pageY;
            move.next({ x: x, y: y, pageX: pageX, pageY: pageY, movementX: movementX, movementY: movementY, buttons: buttons, button: button, modifiers: getModifierKeys(), inside: inside, onElement: ev.target === element });
            if (dragging === DraggingState.Stopped)
                return;
            if (noTextSelect) {
                (_a = ev.preventDefault) === null || _a === void 0 ? void 0 : _a.call(ev);
            }
            Vec2.div(pointerDelta, Vec2.sub(pointerDelta, pointerEnd, pointerStart), getClientSize(rectSize));
            if (Vec2.magnitude(pointerDelta) < EPSILON)
                return;
            var isStart = dragging === DraggingState.Started;
            if (isStart && !mask(ev.clientX, ev.clientY))
                return;
            if (Vec2.distance(pointerEnd, pointerDown) >= 4) {
                hasMoved = true;
            }
            var dx = pointerDelta[0], dy = pointerDelta[1];
            drag.next({ x: x, y: y, dx: dx, dy: dy, pageX: pageX, pageY: pageY, buttons: buttons, button: button, modifiers: getModifierKeys(), isStart: isStart });
            Vec2.copy(pointerStart, pointerEnd);
            dragging = DraggingState.Moving;
        }
        function onMouseWheel(ev) {
            if (!mask(ev.clientX, ev.clientY))
                return;
            eventOffset(pointerEnd, ev);
            var _a = getPagePosition(ev), pageX = _a.pageX, pageY = _a.pageY;
            var x = pointerEnd[0], y = pointerEnd[1];
            if (noScroll) {
                ev.preventDefault();
            }
            var normalized = normalizeWheel(ev);
            buttons = button = ButtonsType.Flag.Auxilary;
            if (normalized.dx || normalized.dy || normalized.dz) {
                wheel.next(__assign(__assign({ x: x, y: y, pageX: pageX, pageY: pageY }, normalized), { buttons: buttons, button: button, modifiers: getModifierKeys() }));
            }
        }
        function tryPreventGesture(ev) {
            var _a, _b;
            // console.log(ev, preventGestures);
            if (!preventGestures)
                return;
            ev.preventDefault();
            (_a = ev.stopImmediatePropagation) === null || _a === void 0 ? void 0 : _a.call(ev);
            (_b = ev.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(ev);
        }
        var prevGestureScale = 0, prevGestureRotation = 0;
        function onGestureStart(ev) {
            tryPreventGesture(ev);
            prevGestureScale = ev.scale;
            prevGestureRotation = ev.rotation;
            gesture.next({ scale: ev.scale, rotation: ev.rotation, deltaRotation: 0, deltaScale: 0, isStart: true });
        }
        function gestureDelta(ev, isEnd) {
            gesture.next({
                scale: ev.scale,
                rotation: ev.rotation,
                deltaRotation: prevGestureRotation - ev.rotation,
                deltaScale: prevGestureScale - ev.scale,
                isEnd: isEnd
            });
            prevGestureRotation = ev.rotation;
            prevGestureScale = ev.scale;
        }
        function onGestureChange(ev) {
            tryPreventGesture(ev);
            gestureDelta(ev);
        }
        function onGestureEnd(ev) {
            tryPreventGesture(ev);
            gestureDelta(ev, true);
        }
        function onResize() {
            resize.next({});
        }
        function insideBounds(pos) {
            if (element instanceof Window || element instanceof Document || element === document.body) {
                return true;
            }
            else {
                var rect = element.getBoundingClientRect();
                return pos[0] >= 0 && pos[1] >= 0 && pos[0] < rect.width && pos[1] < rect.height;
            }
        }
        function getClientSize(out) {
            out[0] = element.clientWidth;
            out[1] = element.clientHeight;
            return out;
        }
        function eventOffset(out, ev) {
            width = element.clientWidth * pixelRatio();
            height = element.clientHeight * pixelRatio();
            if (isLocked) {
                var pr = pixelRatio();
                out[0] = (lockedViewport.x + lockedViewport.width / 2) / pr;
                out[1] = (height - (lockedViewport.y + lockedViewport.height / 2)) / pr;
            }
            else {
                var rect = element.getBoundingClientRect();
                out[0] = (ev.clientX || 0) - rect.left;
                out[1] = (ev.clientY || 0) - rect.top;
            }
            return out;
        }
        function getPagePosition(ev) {
            if (isLocked) {
                return {
                    pageX: Math.round(window.innerWidth / 2) + lockedViewport.x,
                    pageY: Math.round(window.innerHeight / 2) + lockedViewport.y
                };
            }
            else {
                return {
                    pageX: ev.pageX,
                    pageY: ev.pageY
                };
            }
        }
        var crossWidth = 30;
        var cross = addCross();
        function addCross() {
            var _a;
            var cross = document.createElement('div');
            var b = '30%';
            var t = '10%';
            var c = "#000 ".concat(b, ", #0000 0 calc(100% - ").concat(b, "), #000 0");
            var vline = "linear-gradient(0deg, ".concat(c, ") 50%/").concat(t, " 100% no-repeat");
            var hline = "linear-gradient(90deg, ".concat(c, ") 50%/100% ").concat(t, " no-repeat");
            var cdot = 'radial-gradient(circle at 50%, #000 5%, #0000 5%)';
            Object.assign(cross.style, {
                width: "".concat(crossWidth, "px"),
                aspectRatio: 1,
                background: "".concat(vline, ", ").concat(hline, ", ").concat(cdot),
                display: 'none',
                zIndex: 1000,
                position: 'absolute',
                mixBlendMode: 'difference',
                filter: 'invert(1)',
            });
            (_a = element.parentElement) === null || _a === void 0 ? void 0 : _a.appendChild(cross);
            return cross;
        }
        function toggleCross(value) {
            cross.style.display = value ? 'block' : 'none';
            if (value) {
                var pr = pixelRatio();
                var offsetX = (lockedViewport.x + lockedViewport.width / 2) / pr;
                var offsetY = (lockedViewport.y + lockedViewport.height / 2) / pr;
                cross.style.width = "".concat(crossWidth, "px");
                cross.style.left = "calc(".concat(offsetX, "px - ").concat(crossWidth / 2, "px)");
                cross.style.bottom = "calc(".concat(offsetY, "px - ").concat(crossWidth / 2, "px)");
            }
        }
        return __assign(__assign({ get noScroll() { return noScroll; },
            set noScroll(value) { noScroll = value; },
            get noContextMenu() { return noContextMenu; },
            set noContextMenu(value) { noContextMenu = value; },
            get width() { return width; },
            get height() { return height; },
            get pixelRatio() { return pixelRatio(); },
            get pointerLock() { return isLocked; } }, events), { requestPointerLock: function (viewport) {
                lockedViewport = viewport;
                if (!isLocked) {
                    element.requestPointerLock();
                }
            }, exitPointerLock: function () {
                if (isLocked) {
                    element.ownerDocument.exitPointerLock();
                }
            }, dispose: dispose });
    }
    InputObserver.fromElement = fromElement;
})(InputObserver || (InputObserver = {}));
// Adapted from https://stackoverflow.com/a/30134826
// License: https://creativecommons.org/licenses/by-sa/3.0/
function normalizeWheel(event) {
    // Reasonable defaults
    var PIXEL_STEP = 10;
    var LINE_HEIGHT = 40;
    var PAGE_HEIGHT = 800;
    var spinX = 0, spinY = 0, dx = 0, dy = 0, dz = 0; // pixelX, pixelY, pixelZ
    // Legacy
    if ('detail' in event) {
        spinY = event.detail;
    }
    if ('wheelDelta' in event) {
        spinY = -event.wheelDelta / 120;
    }
    if ('wheelDeltaY' in event) {
        spinY = -event.wheelDeltaY / 120;
    }
    if ('wheelDeltaX' in event) {
        spinX = -event.wheelDeltaX / 120;
    }
    // side scrolling on FF with DOMMouseScroll
    if ('axis' in event && event.axis === event.HORIZONTAL_AXIS) {
        spinX = spinY;
        spinY = 0;
    }
    dx = spinX * PIXEL_STEP;
    dy = spinY * PIXEL_STEP;
    if ('deltaY' in event) {
        dy = event.deltaY;
    }
    if ('deltaX' in event) {
        dx = event.deltaX;
    }
    if ('deltaZ' in event) {
        dz = event.deltaZ;
    }
    if ((dx || dy || dz) && event.deltaMode) {
        if (event.deltaMode === 1) { // delta in LINE units
            dx *= LINE_HEIGHT;
            dy *= LINE_HEIGHT;
            dz *= LINE_HEIGHT;
        }
        else { // delta in PAGE units
            dx *= PAGE_HEIGHT;
            dy *= PAGE_HEIGHT;
            dz *= PAGE_HEIGHT;
        }
    }
    // Fall-back if spin cannot be determined
    if (dx && !spinX) {
        spinX = (dx < 1) ? -1 : 1;
    }
    if (dy && !spinY) {
        spinY = (dy < 1) ? -1 : 1;
    }
    return { spinX: spinX, spinY: spinY, dx: dx, dy: dy, dz: dz };
}
export { InputObserver };
